import { Component, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SubscriptionApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/subscription/subscription-api';
import { BaseToastComponent } from '../../../@commons/ui-components/toast-popup/base-toast-component';
import { SharedModule } from '../../../@commons/ui-components/shared.module';
import { CheckoutService } from '../../../@commons/@services/front/checkout.service';
import { PremiumService } from '../../../@commons/@services/front/premium.service';

/**
 * « Mon abonnement » : statut, échéance, annulation et réactivation.
 *
 * L'annulation n'est jamais un piège : elle est accessible en deux clics, et le
 * message dit exactement jusqu'à quand l'accès reste ouvert — fin du mois pour
 * un mensuel, fin de l'année pour un annuel. Tant que la période court, un
 * simple bouton « Réactiver » revient en arrière.
 */
@Component({
  selector: 'vw-subscription-toast',
  imports: [SharedModule, CommonModule, TranslateModule],
  templateUrl: './subscription-toast.component.html',
  providers: [{ provide: SubscriptionApi, useClass: SubscriptionApi }],
})
export class SubscriptionToastComponent
  extends BaseToastComponent
  implements OnChanges
{
  loading = false;
  confirmingCancel = false;
  error = false;

  readonly premiumService = inject(PremiumService);
  private readonly checkoutService = inject(CheckoutService);
  private readonly translate = inject(TranslateService);

  constructor(private readonly subscriptionApi: SubscriptionApi) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue) {
      this.confirmingCancel = false;
      this.error = false;
      void this.premiumService.refresh();
    }
  }

  get state() {
    return this.premiumService.subscription();
  }

  /** Seul un abonnement Stripe se pilote : un essai ou un cadeau n'a rien à annuler. */
  get isStripe(): boolean {
    return this.state?.provider === 'STRIPE';
  }

  get isCanceled(): boolean {
    return Boolean(this.state?.cancelAtPeriodEnd);
  }

  /** Libellé d'échéance adapté au canal et à l'état. */
  get statusLabel(): string {
    const state = this.state;
    if (!state?.currentPeriodEnd) return '';
    const date = new Date(state.currentPeriodEnd).toLocaleDateString();

    if (state.provider === 'TRIAL') {
      return this.translate.instant('premium.manage.trial-until', { date });
    }
    if (state.provider === 'MANUAL') {
      return this.translate.instant('premium.manage.gifted-until', { date });
    }
    if (state.cancelAtPeriodEnd) {
      return this.translate.instant('premium.manage.ends-on', { date });
    }
    return this.translate.instant('premium.manage.active-until', { date });
  }

  /** Essai ou mois offerts : rien n'est facturé, rien n'est programmé. */
  get isGifted(): boolean {
    return (
      this.state?.provider === 'TRIAL' || this.state?.provider === 'MANUAL'
    );
  }

  /** Échéance courante, formatée. */
  get periodEndLabel(): string {
    return this.state?.currentPeriodEnd
      ? new Date(this.state.currentPeriodEnd).toLocaleDateString()
      : '';
  }

  /**
   * Le message de confirmation dit la vérité, et elle diffère selon le canal.
   *
   * Un abonnement payé court jusqu'à l'échéance déjà réglée. Un essai ou des
   * mois offerts s'arrêtent SUR-LE-CHAMP : il n'y a rien à facturer, donc rien
   * à faire courir. Le dire clairement évite de laisser croire qu'on garde ses
   * jours restants.
   */
  get cancelConfirmMessage(): string {
    const date = this.periodEndLabel;
    if (this.isGifted) {
      return this.translate.instant('premium.manage.cancel-confirm-gifted');
    }
    const key =
      this.state?.plan === 'YEARLY'
        ? 'premium.manage.cancel-confirm-yearly'
        : 'premium.manage.cancel-confirm-monthly';
    return this.translate.instant(key, { date });
  }

  /** Libellé du bouton de résiliation, adapté au canal. */
  get cancelLabel(): string {
    return this.isGifted
      ? 'premium.manage.stop-gifted'
      : 'premium.manage.cancel';
  }

  async confirmCancel(): Promise<void> {
    await this.run(() => this.subscriptionApi.cancel());
    this.confirmingCancel = false;
  }

  async reactivate(): Promise<void> {
    await this.run(() => this.subscriptionApi.reactivate());
  }

  async openBilling(): Promise<void> {
    this.error = false;
    try {
      await this.checkoutService.manageBilling();
    } catch {
      this.error = true;
    }
  }

  private async run(action: () => Promise<unknown>): Promise<void> {
    this.loading = true;
    this.error = false;
    try {
      await action();
      await this.premiumService.refresh();
    } catch {
      this.error = true;
    } finally {
      this.loading = false;
    }
  }
}
